import { useMemo, useCallback } from 'react';
import {
  Typography,
  Avatar,
  Box,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { useLanguage } from '../../context/LanguageContext';
import '../../assets/style/TransactionCard.css';

export default function TransactionCard({ transaction }) {
  const {
    status,
    created_at,
    amount,
    comment,
    type,
    from,
    to,
    currency,
    asset_ticker,
  } = transaction;

  const { t } = useLanguage();

  // Centralized config: status -> icon + color
  const STATUS_CONFIG = useMemo(
    () => ({
      completed: { color: '#22c55e', icon: <CheckCircleIcon /> },
      success: { color: '#22c55e', icon: <CheckCircleIcon /> },
      pending: { color: '#f59e0b', icon: <AccessTimeIcon /> },
      processing: { color: '#3b82f6', icon: <AutorenewIcon /> },
      stuck: { color: '#a855f7', icon: <PauseCircleOutlineIcon /> },
      cancelled: { color: '#6b7280', icon: <PauseCircleOutlineIcon /> },
      canceled: { color: '#6b7280', icon: <PauseCircleOutlineIcon /> },
      failed: { color: '#ef4444', icon: <ErrorIcon /> },
      error: { color: '#ef4444', icon: <ErrorIcon /> },
      default: { color: '#6b7280', icon: <HelpOutlineIcon /> },
    }),
    []
  );

  // Type colors
  const TYPE_CONFIG = useMemo(
    () => ({
      deposit:    { color: '#22c55e' }, // incoming
      withdrawal: { color: '#ef4444' }, // outgoing
      transfer:   { color: '#3b82f6' }, // internal
      send:       { color: '#ef4444' }, // alias of withdrawal
      default:    { color: 'rgba(255,255,255,0.10)' },
    }),
    []
  );

  var type_deposit = type;
  if (type_deposit==='receive')
    type_deposit = 'deposit';
  else if (type_deposit==='send')
    type_deposit = 'withdrawal';


  const normalizedStatus = (status || '').toString().toLowerCase();
  const normalizedType = (type_deposit || '').toString().toLowerCase();

  const statusCfg = STATUS_CONFIG[normalizedStatus] || STATUS_CONFIG.default;
  const typeCfg = TYPE_CONFIG[normalizedType] || TYPE_CONFIG.default;
  const displayStatus = useMemo(() => {
    if (!status) return 'Unknown';

    const translatedStatus = t(normalizedStatus);
    if (translatedStatus !== normalizedStatus) {
      return translatedStatus;
    }

    return status
      .toString()
      .replace(/[_-]+/g, ' ')
      .replace(/\b[a-z]/g, (char) => char.toUpperCase());
  }, [normalizedStatus, status, t]);

  // Date/time formatting
  const dt = useMemo(() => {
    if (!created_at) return null;
    const d = new Date(created_at);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [created_at]);

  const dateStr = useMemo(() => (dt ? dt.toLocaleDateString('en-GB') : ''), [dt]);
  const timeStr = useMemo(
    () => (dt ? dt.toLocaleTimeString('en-GB', { hour12: false }) : ''),
    [dt]
  );

  // Copy helper
  const copyToClipboard = useCallback(async (text) => {
    try {
      if (!text) return;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        return;
      }
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    } catch (e) {
      // Silent fail – avoid breaking UI
      // console.error(e);
    }
  }, []);

  // Amount formatting (optional sign by type)
  const amountSign = useMemo(() => {
    if (normalizedType === 'withdrawal') return '-';
    if (normalizedType === 'deposit') return '+';
    return '';
  }, [normalizedType]);

  const amountColor = useMemo(() => {
    if (normalizedType === 'withdrawal') return '#ef4444';
    if (normalizedType === 'deposit') return '#22c55e';
    return '#22c55e'; // keep your current green accent for neutral types
  }, [normalizedType]);

  const ticker = asset_ticker || currency || 'USD';

  return (
    <Box
      sx={{
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: 2,
        border: '1px solid rgba(255, 255, 255, 0.10)',
        padding: { xs: 2, sm: 3 },
        marginBottom: 2,
        transition: 'all 0.25s ease',
        '&:hover': {
          borderColor: 'rgba(99, 102, 241, 0.35)',
          backgroundColor: 'rgba(255, 255, 255, 0.07)',
          boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1.5, sm: 0 },
        }}
      >
        {/* Left: status + date */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              backgroundColor: statusCfg.color,
              color: 'white',
              flex: '0 0 auto',
            }}
          >
            {statusCfg.icon}
          </Avatar>

          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  color: statusCfg.color,
                  fontWeight: 'bold',
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                }}
              >
                {displayStatus}
              </Typography>

              {/* Optional: hint for stuck */}
              {normalizedStatus === 'stuck' && (
                <Tooltip title="This transaction requires attention (stuck).">
                  <HelpOutlineIcon sx={{ fontSize: 18, opacity: 0.75 }} />
                </Tooltip>
              )}
            </Box>

            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255, 255, 255, 0.70)',
                fontSize: { xs: '0.8rem', sm: '0.85rem' },
              }}
            >
              {dateStr && timeStr ? `${dateStr} • ${timeStr}` : created_at}
            </Typography>
          </Box>

        </Box>

        {/* Right: amount + type */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'flex-start', sm: 'flex-end' },
            gap: 1,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: amountColor,
              fontWeight: 900,
              fontSize: { xs: '1.125rem', sm: '1.25rem' },
              fontFamily: 'monospace',
              letterSpacing: '0.4px',
            }}
          >
            {amountSign}
            {amount} {ticker}
          </Typography>

          {type && (
            <Chip
              label={normalizedType}
              size="small"
              sx={{
                backgroundColor: typeCfg.color,
                color: 'rgba(255,255,255,0.92)',
                fontWeight: 800,
                border: '1px solid rgba(255,255,255,0.16)',
                textTransform: 'capitalize',
              }}
            />
          )}
        </Box>
      </Box>

      {/* Divider */}
      <Box
        sx={{
          borderTop: '1px solid rgba(255,255,255,0.08)',
          mt: 2,
          pt: 2,
        }}
      />

      {/* Details */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
          },
          gap: { xs: 1.5, sm: 2 },
        }}
      >
        {/* From */}
        {from && (
          <Box>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.65)',
                marginBottom: 0.5,
              }}
            >
              {t('from')}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title={from}>
                <Typography
                  variant="body1"
                  noWrap
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    whiteSpace: { xs: 'normal', sm: 'nowrap' },
                    wordBreak: { xs: 'break-all', sm: 'normal' },
                    maxWidth: { xs: '100%', sm: 240, md: 280 },
                  }}
                >
                  {from}
                </Typography>
              </Tooltip>

              <Tooltip title="Copy">
                <IconButton
                  size="small"
                  onClick={() => copyToClipboard(from)}
                  sx={{ color: 'rgba(255,255,255,0.75)' }}
                >
                  <ContentCopyIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}

        {/* To */}
        {to && (
          <Box>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.65)',
                marginBottom: 0.5,
              }}
            >
              {t('to')}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title={to}>
                <Typography
                  variant="body1"
                  noWrap
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    whiteSpace: { xs: 'normal', sm: 'nowrap' },
                    wordBreak: { xs: 'break-all', sm: 'normal' },
                    maxWidth: { xs: '100%', sm: 240, md: 280 },
                  }}
                >
                  {to}
                </Typography>
              </Tooltip>

              <Tooltip title="Copy">
                <IconButton
                  size="small"
                  onClick={() => copyToClipboard(to)}
                  sx={{ color: 'rgba(255,255,255,0.75)' }}
                >
                  <ContentCopyIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}

        {/* Transaction Type */}
        {type && (
          <Box>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.65)',
                marginBottom: 0.5,
              }}
            >
              {t('transactionType')}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: 'white',
                fontWeight: 600,
                textTransform: 'capitalize',
              }}
            >
              {normalizedType}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Comment */}
      {comment && (
        <Box
          sx={{
            padding: { xs: 1.5, sm: 2 },
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
            borderRadius: 1,
            border: '1px solid rgba(255, 255, 255, 0.10)',
            marginTop: { xs: 2, sm: 2 },
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255,255,255,0.85)',
              fontSize: { xs: '0.78rem', sm: '0.85rem' },
              lineHeight: 1.4,
              fontFamily: 'monospace',
            }}
          >
            {comment}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
